import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Loader2, Star, MessageCircle, ExternalLink } from 'lucide-react';

const emptyForm = { name: '', role: '', avatar: '', text: '', rating: 5, sort_order: 0, is_active: true };

function deriveInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function StarRow({ value, max = 5, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Star key={i} size={size} className={i < value ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} />
      ))}
    </div>
  );
}

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/testimonials');
      setItems(res.data.testimonials || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat testimoni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: (items[items.length - 1]?.sort_order ?? items.length - 1) + 1 });
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name || '',
      role: it.role || '',
      avatar: it.avatar || '',
      text: it.text || '',
      rating: it.rating ?? 5,
      sort_order: it.sort_order ?? 0,
      is_active: it.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error('Nama dan testimoni wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        avatar: (form.avatar || '').trim() || deriveInitials(form.name),
        rating: Math.max(1, Math.min(5, Number(form.rating) || 5)),
      };
      if (editingId) {
        await api.put(`/admin/testimonials/${editingId}`, payload);
        toast.success('Testimoni diperbarui');
      } else {
        await api.post('/admin/testimonials', payload);
        toast.success('Testimoni ditambahkan');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Hapus testimoni dari "${it.name}"?`)) return;
    try {
      await api.delete(`/admin/testimonials/${it.id}`);
      toast.success('Testimoni dihapus');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/testimonials/${it.id}`, {
        name: it.name, role: it.role, avatar: it.avatar, text: it.text,
        rating: it.rating, sort_order: it.sort_order, is_active: !it.is_active,
      });
      fetchItems();
    } catch {
      toast.error('Gagal mengubah status');
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx];
    const b = items[target];
    try {
      await Promise.all([
        api.put(`/admin/testimonials/${a.id}`, { ...a, sort_order: b.sort_order }),
        api.put(`/admin/testimonials/${b.id}`, { ...b, sort_order: a.sort_order }),
      ]);
      fetchItems();
    } catch {
      toast.error('Gagal mengubah urutan');
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kelola Testimoni</h1>
          <p className="text-sm text-slate-500 mt-1">Testimoni yang tampil di section "Apa Kata Mereka" di halaman beranda Tokomoe.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href="/#testimonials" target="_blank" rel="noopener noreferrer"><ExternalLink size={14} className="mr-1.5" />Lihat di Landing</a>
          </Button>
          <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-testimonial-btn">
            <Plus size={16} className="mr-1.5" /> Tambah Testimoni
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center text-slate-500"><Loader2 size={20} className="animate-spin mr-2" /> Memuat...</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-16 text-center">
            <MessageCircle size={32} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-500 mb-4">Belum ada testimoni. Tambahkan yang pertama.</p>
            <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"><Plus size={16} className="mr-1.5" /> Tambah Testimoni</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <Card key={it.id} className={`border-slate-200 ${!it.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className="flex flex-col gap-1 pt-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Naik"><ArrowUp size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Turun"><ArrowDown size={14} /></Button>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center text-sm font-bold text-[#2D6CDF] flex-shrink-0">
                  {it.avatar || deriveInitials(it.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900">{it.name}</p>
                    <StarRow value={it.rating || 5} />
                  </div>
                  {it.role && <p className="text-xs text-slate-500">{it.role}</p>}
                  <p className="text-sm text-slate-600 mt-1.5 line-clamp-2 italic">"{it.text}"</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-2 mr-2">
                    <Switch checked={it.is_active} onCheckedChange={() => toggleActive(it)} aria-label="Aktif" />
                    <span className="text-xs text-slate-500 hidden md:inline">{it.is_active ? 'Aktif' : 'Nonaktif'}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(it)} aria-label="Edit"><Edit2 size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(it)} aria-label="Hapus" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Testimoni' : 'Tambah Testimoni'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Preview */}
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <StarRow value={form.rating} size={14} />
              <p className="text-sm text-slate-700 mt-3 italic">"{form.text || 'Isi testimoni akan tampil di sini.'}"</p>
              <div className="flex items-center gap-3 mt-3">
                <div className="w-10 h-10 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center text-sm font-bold text-[#2D6CDF]">
                  {(form.avatar || deriveInitials(form.name)).slice(0, 3).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{form.name || 'Nama Customer'}</p>
                  <p className="text-xs text-slate-500">{form.role || 'Jabatan / Bisnis'}</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 text-center">Preview tampilan di Landing</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nama</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Contoh: Rina Sari"
                  className="mt-1"
                  data-testid="testimonial-name-input"
                />
              </div>
              <div>
                <Label>Jabatan / Bisnis</Label>
                <Input
                  value={form.role}
                  onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
                  placeholder="Contoh: Owner, BatikNusantara.id"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Testimoni</Label>
              <Textarea
                value={form.text}
                onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))}
                placeholder="Tulis ulasan/testimoni dari customer"
                rows={4}
                className="mt-1"
                data-testid="testimonial-text-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Avatar (inisial 2-3 huruf)</Label>
                <Input
                  value={form.avatar}
                  onChange={(e) => setForm((p) => ({ ...p, avatar: e.target.value.toUpperCase() }))}
                  placeholder={deriveInitials(form.name)}
                  maxLength={3}
                  className="mt-1 font-mono"
                />
                <p className="text-[11px] text-slate-500 mt-1">Kosongkan untuk auto dari nama.</p>
              </div>
              <div>
                <Label>Rating</Label>
                <div className="flex items-center gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, rating: n }))}
                      className="p-0.5"
                      aria-label={`${n} bintang`}
                    >
                      <Star size={22} className={n <= form.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-300'} />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-slate-600">{form.rating}/5</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 pt-3">
              <div>
                <Label>Tampilkan di Landing</Label>
                <p className="text-xs text-slate-500">Nonaktifkan untuk menyembunyikan tanpa menghapus.</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="testimonial-save-btn">
              {saving ? <><Loader2 size={16} className="animate-spin mr-2" /> Menyimpan...</> : editingId ? 'Simpan Perubahan' : 'Tambah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
